// ============================================================================
// BOT AI - Rule-based AI controller for bots (implements IBotController)
// ============================================================================

import { Vector2, PlayerInput, ObjectiveState, Team } from '@/shared/types';
import { Vec2 } from '@/shared/utils';
import { Player } from '@/game/entities/Player';
import { GameMap } from '@/game/map/GameMap';
import { CollisionWorld, CollisionLayers } from '@/engine/CollisionSystem';
import { 
  IBotController, 
  BotAIMode, 
  BotGameContext, 
  BotStateInfo, 
  TacticalIntent 
} from './IBotController';

export enum BotState {
  Idle = 'idle',
  Patrol = 'patrol',
  Chase = 'chase',
  Attack = 'attack',
  Retreat = 'retreat',
  Defuse = 'defuse',
  PlantSpike = 'plant_spike',
  Follow = 'follow',  // Follow friendly player (for attacker bots)
}

export interface BotConfig {
  aggressionLevel: number;    // 0-1, how aggressive the bot is
  reactionTime: number;       // ms delay before reacting to threats
  accuracy: number;           // 0-1, how accurate shots are
  patrolRadius: number;       // How far bot patrols from spawn
  sightRange: number;         // How far bot can see enemies
  attackRange: number;        // Range at which bot starts shooting
}

const DEFAULT_BOT_CONFIG: BotConfig = {
  aggressionLevel: 0.6,
  reactionTime: 200,
  accuracy: 0.6,
  patrolRadius: 300,
  sightRange: 500,
  attackRange: 400,
};

/**
 * Rule-based Bot AI Controller
 * Uses state machines and heuristics for decision making
 */
export class RuleBasedBotController implements IBotController {
  readonly type = BotAIMode.RuleBased;
  
  private player: Player;
  private config: BotConfig;
  private state: BotState = BotState.Patrol;
  
  // Current context (updated each frame)
  private currentContext: BotGameContext | null = null;
  private currentObjectives: ObjectiveState | undefined;
  
  // Navigation
  private targetPosition: Vector2 | null = null;
  private patrolCenter: Vector2;
  private lastPatrolChange: number = 0;
  private patrolInterval: number = 3000; // Change patrol point every 3s
  
  // Combat
  private targetEnemy: Player | null = null;
  private lastSeenEnemy: number = 0;
  private lastStateChange: number = 0;
  
  // Stuck detection
  private lastPosition: Vector2;
  private stuckTime: number = 0;
  
  // Spike objectives
  private spikePosition: Vector2 | null = null;
  private spikeSitePosition: Vector2 | null = null;
  
  // Follow behavior
  private followTarget: Player | null = null;
  
  // Collision reference for wall avoidance
  private collisionWorld: CollisionWorld | null = null;
  
  // Separation - nearby bots to avoid
  private nearbyBots: Player[] = [];
  
  // Individual variation (randomness)
  private randomOffset: number;  // Unique offset for this bot
  
  // Tactical intent (can be set by SLM or external systems)
  private tacticalIntent: TacticalIntent | null = null;
  
  // Cached visible enemies for state info
  private cachedVisibleEnemies: Player[] = [];

  constructor(player: Player, config: Partial<BotConfig> = {}) {
    this.player = player;
    this.config = { ...DEFAULT_BOT_CONFIG, ...config };
    this.patrolCenter = { ...player.position };
    this.lastPosition = { ...player.position };
    
    // Generate unique random offset for behavior variation
    this.randomOffset = Math.random() * 1000;
    
    // Randomize patrol interval (2-5 seconds) so bots don't all move at same time
    this.patrolInterval = 2000 + Math.random() * 3000;
  }

  // =========================================================================
  // IBotController Interface Implementation
  // =========================================================================
  
  /**
   * Main update method - generates input for this frame
   * Implements IBotController.update
   */
  update(context: BotGameContext): PlayerInput {
    // Store context for other methods
    this.currentContext = context;
    this.currentObjectives = context.objectives;
    this.collisionWorld = context.collisionWorld;
    
    // Legacy update logic
    return this.updateInternal(
      context.dt,
      context.time,
      context.allPlayers,
      context.collisionWorld,
      context.map,
      context.objectives
    );
  }
  
  /**
   * Get player this controller manages
   */
  getPlayer(): Player {
    return this.player;
  }
  
  /**
   * Get current bot state for SLM or debugging
   */
  getStateInfo(): BotStateInfo {
    const map = this.currentContext?.map;
    const ability1 = this.player.abilities[1];
    const ability2 = this.player.abilities[2];
    
    const stateInfo: BotStateInfo = {
      botId: this.player.id,
      position: { ...this.player.position },
      health: this.player.health,
      shield: this.player.shield,
      team: this.player.team === Team.Attackers ? 'attackers' : 'defenders',
      isAlive: this.player.alive,
      
      ammo: this.player.weapon.ammo,
      maxAmmo: this.player.weapon.maxAmmo,
      isReloading: this.player.weapon.reloading,
      weaponName: this.player.weapon.weaponId,
      
      ability1Ready: ability1 !== undefined && ability1.charges > 0,
      ability2Ready: ability2 !== undefined && ability2.charges > 0,
      ultimateReady: this.player.ultimateCharge >= 100,
      ultimateCharge: this.player.ultimateCharge,
      
      visibleEnemies: this.cachedVisibleEnemies.map(e => ({
        id: e.id,
        position: { ...e.position },
        health: e.health,
        distance: Vec2.distance(this.player.position, e.position),
      })),
      
      nearbyTeammates: this.nearbyBots
        .filter(p => p.team === this.player.team)
        .map(t => ({
          id: t.id,
          position: { ...t.position },
          health: t.health,
          distance: Vec2.distance(this.player.position, t.position),
        })),
      
      hasSpikeCarrier: this.currentObjectives?.spikeCarrierId === this.player.id,
      spikePlanted: this.currentObjectives?.spikePlanted || false,
      inSpikeSite: map ? map.isInSpikeSite(this.player.position) !== -1 : false,
      
      distanceToNearestWall: this.getDistanceToNearestWall(),
      currentZone: this.getCurrentZone(),
    };
    
    // Only add optional fields if they have values
    if (this.currentObjectives?.spikePosition) {
      stateInfo.spikePosition = this.currentObjectives.spikePosition;
    }
    
    return stateInfo;
  }
  
  /**
   * Set tactical intent from external source (SLM)
   */
  setTacticalIntent(intent: TacticalIntent): void {
    this.tacticalIntent = intent;
    // Map intent to internal state
    this.mapIntentToState(intent);
  }
  
  /**
   * Get current tactical intent
   */
  getTacticalIntent(): TacticalIntent | null {
    return this.tacticalIntent;
  }
  
  /**
   * Reset on round start
   */
  onRoundStart(): void {
    this.state = BotState.Patrol;
    this.tacticalIntent = null;
    this.patrolCenter = { ...this.player.position };
    this.targetEnemy = null;
    this.followTarget = null;
  }
  
  /**
   * Cleanup on round end
   */
  onRoundEnd(): void {
    this.tacticalIntent = null;
  }
  
  /**
   * Dispose resources
   */
  dispose(): void {
    // Nothing to clean up for rule-based AI
  }
  
  // =========================================================================
  // Helper Methods for State Info
  // =========================================================================
  
  private getDistanceToNearestWall(): number {
    if (!this.collisionWorld) return 999;
    
    // Check 8 directions
    const directions = [
      { x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 },
      { x: 0.707, y: 0.707 }, { x: -0.707, y: 0.707 },
      { x: 0.707, y: -0.707 }, { x: -0.707, y: -0.707 },
    ];
    
    let minDist = 999;
    for (const dir of directions) {
      const hit = this.collisionWorld.raycast(
        this.player.position,
        dir,
        200,
        CollisionLayers.WALL
      );
      if (hit && hit.distance < minDist) {
        minDist = hit.distance;
      }
    }
    return minDist;
  }
  
  private getCurrentZone(): string {
    const map = this.currentContext?.map;
    if (!map) return 'unknown';
    
    const siteIndex = map.isInSpikeSite(this.player.position);
    if (siteIndex === 0) return 'site_a';
    if (siteIndex === 1) return 'site_b';
    
    // Simple zone detection based on position
    const width = map.getWidth();
    const height = map.getHeight();
    const x = this.player.position.x / width;
    const y = this.player.position.y / height;
    
    if (x < 0.3) return 'left';
    if (x > 0.7) return 'right';
    if (y < 0.3) return 'top';
    if (y > 0.7) return 'bottom';
    return 'mid';
  }
  
  private mapIntentToState(intent: TacticalIntent): void {
    switch (intent) {
      case TacticalIntent.Aggressive:
        this.state = BotState.Chase;
        break;
      case TacticalIntent.Retreating:
        this.state = BotState.Retreat;
        break;
      case TacticalIntent.Defensive:
        this.state = BotState.Patrol;
        break;
      case TacticalIntent.Flanking:
        this.state = BotState.Patrol; // Will patrol to a different area
        break;
      case TacticalIntent.Supporting:
        this.state = BotState.Follow;
        break;
      case TacticalIntent.Objective:
        // For objective, check if we should plant or defuse
        if (this.player.team === Team.Attackers) {
          this.state = BotState.PlantSpike;
        } else {
          this.state = BotState.Defuse;
        }
        break;
      case TacticalIntent.Patrolling:
      default:
        this.state = BotState.Patrol;
        break;
    }
  }
  
  // =========================================================================
  // Internal Update Logic (Original BotAI logic)
  // =========================================================================

  private updateInternal(
    dt: number,
    time: number,
    allPlayers: Player[],
    collisionWorld: CollisionWorld,
    _map: GameMap,
    objectives?: ObjectiveState
  ): PlayerInput {
    // Store collision world for wall avoidance
    this.collisionWorld = collisionWorld;
    
    // Check if stuck
    this.checkStuck(dt);
    
    // Separate enemies and teammates
    const enemies = allPlayers.filter(p => p.team !== this.player.team && p.alive);
    const teammates = allPlayers.filter(p => p.team === this.player.team && p.alive && p.id !== this.player.id);
    
    // Track nearby bots for separation (both teams, within 80 pixels)
    this.nearbyBots = allPlayers.filter(p => 
      p.alive && 
      p.id !== this.player.id && 
      Vec2.distance(this.player.position, p.position) < 80
    );
    
    // Find visible enemies
    const visibleEnemies = this.findVisibleEnemies(enemies, collisionWorld);
    this.cachedVisibleEnemies = visibleEnemies; // Cache for getStateInfo
    
    // Find follow target (for friendly bots - follow nearest alive teammate)
    this.findFollowTarget(teammates, objectives);
    
    // Check for spike-related priorities
    const spikeState = this.checkSpikeState(objectives);
    if (spikeState) {
      this.setState(spikeState, time);
    } else if (visibleEnemies.length === 0 && this.followTarget && this.player.team === Team.Attackers) {
      // No enemies visible - friendly attackers should follow teammates
      this.setState(BotState.Follow, time);
    } else {
      // Update state based on situation
      this.updateState(time, visibleEnemies);
    }
    
    // Generate input based on state
    const input = this.generateInput(time, visibleEnemies, collisionWorld);
    
    // Store position for stuck detection
    this.lastPosition = { ...this.player.position };
    
    return input;
  }
  
  // Find a teammate to follow (preferably the spike carrier or nearest human)
  private findFollowTarget(teammates: Player[], objectives?: ObjectiveState): void {
    if (teammates.length === 0) {
      this.followTarget = null;
      return;
    }
    
    // Priority 1: Follow the spike carrier if on same team
    if (objectives?.spikeCarrierId) {
      const spikeCarrier = teammates.find(p => p.id === objectives.spikeCarrierId);
      if (spikeCarrier) {
        this.followTarget = spikeCarrier;
        return;
      }
    }
    
    // Priority 2: Follow nearest teammate
    let nearest: Player | null = null;
    let nearestDist = Infinity;
    for (const teammate of teammates) {
      const dist = Vec2.distance(this.player.position, teammate.position);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = teammate;
      }
    }
    this.followTarget = nearest;
  }

  private checkStuck(dt: number): void {
    const moved = Vec2.distance(this.player.position, this.lastPosition);
    if (moved < 1) {
      this.stuckTime += dt;
      if (this.stuckTime > 1000) {
        // Pick new random target to get unstuck
        this.targetPosition = this.getRandomPatrolPoint();
        this.stuckTime = 0;
      }
    } else {
      this.stuckTime = 0;
    }
  }

  private findVisibleEnemies(enemies: Player[], collisionWorld: CollisionWorld): Player[] {
    const visible: Player[] = [];
    
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      if (enemy.team === this.player.team) continue;
      if (enemy.isInvisible) continue;
      
      const dist = Vec2.distance(this.player.position, enemy.position);
      if (dist > this.config.sightRange) continue;
      
      // Raycast to check line of sight
      const direction = Vec2.normalize(Vec2.sub(enemy.position, this.player.position));
      const hit = collisionWorld.raycast(
        this.player.position,
        direction,
        dist,
        CollisionLayers.WALL
      );
      
      // If we hit a wall before reaching the enemy, they're not visible
      if (hit && hit.distance < dist - 20) continue;
      
      visible.push(enemy);
    }
    
    return visible;
  }

  private updateState(time: number, visibleEnemies: Player[]): void {
    const timeSinceStateChange = time - this.lastStateChange;
    
    if (visibleEnemies.length > 0) {
      // Sort by distance
      visibleEnemies.sort((a, b) => 
        Vec2.distance(this.player.position, a.position) - 
        Vec2.distance(this.player.position, b.position)
      );
      
      this.targetEnemy = visibleEnemies[0]!;
      this.lastSeenEnemy = time;
      
      const dist = Vec2.distance(this.player.position, this.targetEnemy.position);
      
      // Decide to attack or chase
      if (dist <= this.config.attackRange) {
        if (this.player.health < 30 && this.config.aggressionLevel < 0.8) {
          this.setState(BotState.Retreat, time);
        } else {
          this.setState(BotState.Attack, time);
        }
      } else {
        if (this.config.aggressionLevel > 0.4) {
          this.setState(BotState.Chase, time);
        } else {
          this.setState(BotState.Attack, time);
        }
      }
    } else {
      this.targetEnemy = null;
      
      // If recently saw enemy, stay alert
      if (time - this.lastSeenEnemy < 2000) {
        // Stay in current combat state briefly
      } else if (timeSinceStateChange > 2000) {
        this.setState(BotState.Patrol, time);
      }
    }
  }

  private setState(newState: BotState, time: number): void {
    if (this.state !== newState) {
      this.state = newState;
      this.lastStateChange = time;
    }
  }

  private generateInput(
    time: number,
    visibleEnemies: Player[],
    _collisionWorld: CollisionWorld
  ): PlayerInput {
    const input: PlayerInput = {
      moveX: 0,
      moveY: 0,
      aimX: this.player.position.x + Math.cos(this.player.rotation) * 100,
      aimY: this.player.position.y + Math.sin(this.player.rotation) * 100,
      shoot: false,
      reload: false,
      interact: false,
      throwGrenade: false,
      useBombCall: false,
      useMissileStrike: false,
      ability1: false,
      ability2: false,
      ultimate: false,
      weaponSlot: 0,
      sequence: 0,
    };

    switch (this.state) {
      case BotState.Patrol:
        this.doPatrol(time, input);
        break;
        
      case BotState.Chase:
        this.doChase(input);
        break;
        
      case BotState.Attack:
        this.doAttack(time, input, visibleEnemies);
        break;
        
      case BotState.Retreat:
        this.doRetreat(input);
        break;
        
      case BotState.Defuse:
        this.doDefuse(input, this.spikePosition);
        break;
        
      case BotState.PlantSpike:
        this.doPlantSpike(input, this.spikeSitePosition);
        break;
        
      case BotState.Follow:
        this.doFollow(input);
        break;
    }

    // Check if need to reload
    if (this.player.weapon.ammo === 0 && !this.player.weapon.reloading) {
      input.reload = true;
    }
    
    // Apply separation force to avoid overlapping with other bots
    this.applySeparation(input);

    return input;
  }
  
  // Apply separation force to keep distance from nearby bots
  private applySeparation(input: PlayerInput): void {
    if (this.nearbyBots.length === 0) return;
    
    let separationX = 0;
    let separationY = 0;
    
    for (const other of this.nearbyBots) {
      const dist = Vec2.distance(this.player.position, other.position);
      if (dist < 5) continue; // Avoid division by zero
      
      // Calculate push direction (away from other bot)
      const pushDir = Vec2.normalize(Vec2.sub(this.player.position, other.position));
      
      // Stronger push when closer (inverse relationship)
      const strength = Math.max(0, (80 - dist) / 80); // 0 at 80px, 1 at 0px
      
      separationX += pushDir.x * strength;
      separationY += pushDir.y * strength;
    }
    
    // Add separation to movement (blend with existing movement)
    if (separationX !== 0 || separationY !== 0) {
      const sepLen = Math.sqrt(separationX * separationX + separationY * separationY);
      if (sepLen > 0) {
        // Normalize and apply with weight (0.5 = half influence)
        const weight = 0.5;
        input.moveX += (separationX / sepLen) * weight;
        input.moveY += (separationY / sepLen) * weight;
        
        // Re-normalize if over 1
        const len = Math.sqrt(input.moveX * input.moveX + input.moveY * input.moveY);
        if (len > 1) {
          input.moveX /= len;
          input.moveY /= len;
        }
      }
    }
  }

  private doPatrol(time: number, input: PlayerInput): void {
    // Pick new patrol point periodically
    if (!this.targetPosition || time - this.lastPatrolChange > this.patrolInterval) {
      this.targetPosition = this.getRandomPatrolPoint();
      this.lastPatrolChange = time;
    }

    // Move toward patrol point
    this.moveToward(this.targetPosition, input);
    
    // Look in movement direction
    if (input.moveX !== 0 || input.moveY !== 0) {
      const lookAngle = Math.atan2(input.moveY, input.moveX);
      input.aimX = this.player.position.x + Math.cos(lookAngle) * 100;
      input.aimY = this.player.position.y + Math.sin(lookAngle) * 100;
    }
  }

  private doChase(input: PlayerInput): void {
    if (!this.targetEnemy) return;
    
    // Move toward enemy
    this.moveToward(this.targetEnemy.position, input);
    
    // Aim at enemy
    input.aimX = this.targetEnemy.position.x;
    input.aimY = this.targetEnemy.position.y;
  }

  private doAttack(time: number, input: PlayerInput, visibleEnemies: Player[]): void {
    if (!this.targetEnemy || visibleEnemies.length === 0) return;
    
    const dist = Vec2.distance(this.player.position, this.targetEnemy.position);
    
    // Strafe while attacking
    const strafeDir = Math.sin(time / 500) * 0.7;
    const toEnemy = Vec2.normalize(Vec2.sub(this.targetEnemy.position, this.player.position));
    const perpendicular = { x: -toEnemy.y, y: toEnemy.x };
    
    input.moveX = perpendicular.x * strafeDir;
    input.moveY = perpendicular.y * strafeDir;
    
    // Move closer if too far, back up if too close
    if (dist > this.config.attackRange * 0.8) {
      input.moveX += toEnemy.x * 0.5;
      input.moveY += toEnemy.y * 0.5;
    } else if (dist < 100) {
      input.moveX -= toEnemy.x * 0.5;
      input.moveY -= toEnemy.y * 0.5;
    }
    
    // Normalize
    const moveLen = Math.sqrt(input.moveX * input.moveX + input.moveY * input.moveY);
    if (moveLen > 1) {
      input.moveX /= moveLen;
      input.moveY /= moveLen;
    }
    
    // Aim at enemy with some inaccuracy
    const inaccuracy = (1 - this.config.accuracy) * 50;
    input.aimX = this.targetEnemy.position.x + (Math.random() - 0.5) * inaccuracy;
    input.aimY = this.targetEnemy.position.y + (Math.random() - 0.5) * inaccuracy;
    
    // Shoot if we can
    if (!this.player.weapon.reloading && this.player.weapon.ammo > 0) {
      // Add reaction time delay
      if (time - this.lastSeenEnemy > this.config.reactionTime) {
        input.shoot = true;
      }
    }
  }

  private doRetreat(input: PlayerInput): void {
    if (!this.targetEnemy) {
      // Retreat to patrol center
      this.moveToward(this.patrolCenter, input);
    } else {
      // Move away from enemy
      const awayDir = Vec2.normalize(Vec2.sub(this.player.position, this.targetEnemy.position));
      input.moveX = awayDir.x;
      input.moveY = awayDir.y;
      
      // Still aim at enemy while retreating
      input.aimX = this.targetEnemy.position.x;
      input.aimY = this.targetEnemy.position.y;
      
      // Shoot while retreating
      if (!this.player.weapon.reloading && this.player.weapon.ammo > 0) {
        input.shoot = true;
      }
    }
  }

  private moveToward(target: Vector2, input: PlayerInput): void {
    const direction = Vec2.sub(target, this.player.position);
    const dist = Vec2.length(direction);
    
    if (dist > 10) {
      let normalized = Vec2.normalize(direction);
      
      // Wall avoidance - check if we're about to hit a wall
      if (this.collisionWorld) {
        const checkDist = 50; // Check 50 pixels ahead
        const hit = this.collisionWorld.raycast(
          this.player.position,
          normalized,
          checkDist,
          CollisionLayers.WALL
        );
        
        if (hit && hit.distance < checkDist) {
          // Wall ahead! Try to find a way around
          // Check left and right
          const leftDir = { x: -normalized.y, y: normalized.x };
          const rightDir = { x: normalized.y, y: -normalized.x };
          
          const leftHit = this.collisionWorld.raycast(
            this.player.position,
            leftDir,
            checkDist,
            CollisionLayers.WALL
          );
          const rightHit = this.collisionWorld.raycast(
            this.player.position,
            rightDir,
            checkDist,
            CollisionLayers.WALL
          );
          
          // Pick the direction with more space
          const leftDist = leftHit ? leftHit.distance : checkDist;
          const rightDist = rightHit ? rightHit.distance : checkDist;
          
          if (leftDist > rightDist && leftDist > 20) {
            // Blend left with forward direction
            normalized = Vec2.normalize({
              x: normalized.x * 0.3 + leftDir.x * 0.7,
              y: normalized.y * 0.3 + leftDir.y * 0.7
            });
          } else if (rightDist > 20) {
            // Blend right with forward direction
            normalized = Vec2.normalize({
              x: normalized.x * 0.3 + rightDir.x * 0.7,
              y: normalized.y * 0.3 + rightDir.y * 0.7
            });
          }
          // If both sides are blocked, the stuck detection will handle it
        }
      }
      
      input.moveX = normalized.x;
      input.moveY = normalized.y;
    }
  }

  private getRandomPatrolPoint(): Vector2 {
    // Use bot's unique random offset for seeded variation
    const timeOffset = Date.now() + this.randomOffset;
    const angle = (Math.random() + (timeOffset % 1000) / 1000) * Math.PI * 2;
    const radius = (Math.random() * 0.5 + 0.5) * this.config.patrolRadius; // At least 50% of patrol radius
    
    // Add unique offset based on bot ID to spread bots out
    const idOffset = (this.player.id.charCodeAt(0) + this.player.id.charCodeAt(this.player.id.length - 1)) % 360;
    const offsetAngle = angle + (idOffset * Math.PI / 180);
    
    return {
      x: this.patrolCenter.x + Math.cos(offsetAngle) * radius,
      y: this.patrolCenter.y + Math.sin(offsetAngle) * radius,
    };
  }

  // Check if bot should prioritize spike objectives
  private checkSpikeState(objectives?: ObjectiveState): BotState | null {
    if (!objectives) return null;
    
    // Defender: If spike is planted, rush to defuse
    if (this.player.team === Team.Defenders && objectives.spikePlanted && objectives.spikePosition) {
      const dist = Vec2.distance(this.player.position, objectives.spikePosition);
      this.spikePosition = objectives.spikePosition;
      
      // If close enough to defuse (within 60 pixels), defuse
      if (dist <= 60) {
        return BotState.Defuse;
      }
      // Otherwise, run toward spike (unless enemy is very close)
      if (dist > 60 && (!this.targetEnemy || Vec2.distance(this.player.position, this.targetEnemy.position) > 150)) {
        return BotState.Defuse; // Will move toward spike first
      }
    }
    
    // Attacker: If carrying spike, try to plant
    if (this.player.team === Team.Attackers && objectives.spikeCarrierId === this.player.id) {
      // Check if in a spike site - for now, just set a target position toward center of map
      // The bot will need to reach a spike site to plant
      this.spikeSitePosition = { x: 600, y: 400 }; // Default to approximate center
      return BotState.PlantSpike;
    }
    
    return null;
  }

  // Move to spike and hold E to defuse
  private doDefuse(input: PlayerInput, spikePos: Vector2 | null): void {
    if (!spikePos) return;
    
    const dist = Vec2.distance(this.player.position, spikePos);
    
    if (dist > 50) {
      // Move toward spike
      this.moveToward(spikePos, input);
      
      // Look toward spike
      input.aimX = spikePos.x;
      input.aimY = spikePos.y;
    } else {
      // Close enough - hold E to defuse
      input.interact = true;
      input.moveX = 0;
      input.moveY = 0;
      
      // Look around for enemies while defusing
      input.aimX = spikePos.x + Math.cos(Date.now() / 500) * 100;
      input.aimY = spikePos.y + Math.sin(Date.now() / 500) * 100;
    }
  }

  // Move to spike site and hold E to plant
  private doPlantSpike(input: PlayerInput, sitePos: Vector2 | null): void {
    if (!sitePos) return;
    
    const dist = Vec2.distance(this.player.position, sitePos);
    
    if (dist > 80) {
      // Move toward spike site
      this.moveToward(sitePos, input);
      
      // Look in movement direction
      const dir = Vec2.normalize(Vec2.sub(sitePos, this.player.position));
      input.aimX = this.player.position.x + dir.x * 100;
      input.aimY = this.player.position.y + dir.y * 100;
    } else {
      // In spike site - hold E to plant
      input.interact = true;
      input.moveX = 0;
      input.moveY = 0;
    }
  }
  
  // Follow a teammate
  private doFollow(input: PlayerInput): void {
    if (!this.followTarget) return;
    
    const dist = Vec2.distance(this.player.position, this.followTarget.position);
    
    // Keep some distance - don't crowd the player
    if (dist > 120) {
      // Move toward teammate but slightly behind/offset
      const toTarget = Vec2.normalize(Vec2.sub(this.followTarget.position, this.player.position));
      
      // Add some offset so bots spread out
      const offset = (this.player.id.charCodeAt(0) % 3 - 1) * 0.3;
      const perpendicular = { x: -toTarget.y * offset, y: toTarget.x * offset };
      
      input.moveX = toTarget.x + perpendicular.x;
      input.moveY = toTarget.y + perpendicular.y;
      
      // Normalize
      const len = Math.sqrt(input.moveX * input.moveX + input.moveY * input.moveY);
      if (len > 1) {
        input.moveX /= len;
        input.moveY /= len;
      }
      
      // Look in movement direction
      input.aimX = this.player.position.x + toTarget.x * 100;
      input.aimY = this.player.position.y + toTarget.y * 100;
    } else {
      // Close enough - stop and look around for threats
      input.moveX = 0;
      input.moveY = 0;
      
      // Look in same direction as teammate
      const lookDir = { 
        x: Math.cos(this.followTarget.rotation), 
        y: Math.sin(this.followTarget.rotation) 
      };
      input.aimX = this.player.position.x + lookDir.x * 100;
      input.aimY = this.player.position.y + lookDir.y * 100;
    }
  }
}

// Export alias for backward compatibility
export { RuleBasedBotController as BotAI };
